import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import {
  ShieldCheck, ArrowRight, Wallet, Zap, Headphones, FileCheck, BarChart3, Users,
  Phone, Mail, MapPin, Star, CheckCircle,
  LayoutDashboard, FileWarning, DollarSign, Clock, TrendingUp, CreditCard,
  Gauge, Settings, Eye, BadgeCheck, Timer, Banknote,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LandingPage() {
  const emptyForm = { company_name: '', owner_name: '', email: '', phone: '', gstin: '' };
  const [leadForm, setLeadForm] = useState({ ...emptyForm });
  const [submitting, setSubmitting] = useState(false);

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    if (!leadForm.company_name || !leadForm.owner_name || !leadForm.email || !leadForm.phone || leadForm.phone.length < 10) {
      toast.error('Please fill all required fields with a valid 10-digit phone number');
      return;
    }
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leadForm),
      });
      toast.success('Thank you! Our team will contact you within 24 hours.');
      setLeadForm({ ...emptyForm });
    } catch {
      toast.success('Thank you! Our team will contact you within 24 hours.');
      setLeadForm({ ...emptyForm });
    } finally {
      setSubmitting(false);
    }
  };

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleChange = (field, value) => setLeadForm(prev => ({ ...prev, [field]: value }));

  const inputCls = 'h-11 border-[#E2E8F0] focus:border-[#005DE8]';
  const inputClsDark = 'h-11 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:border-white';

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
      {/* ===== NAVBAR ===== */}
      <nav className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-[#005DE8] rounded-lg flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-[#0F172A]">Mobikoo</span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <button onClick={() => scrollTo('how-it-works')} className="text-sm text-[#475569] hover:text-[#005DE8] transition-colors">How it works</button>
            <button onClick={() => scrollTo('benefits')} className="text-sm text-[#475569] hover:text-[#005DE8] transition-colors">Partner benefits</button>
            <Link to="/verify" className="text-sm text-[#475569] hover:text-[#005DE8] transition-colors">Check Warranty</Link>
            <Link to="/claim" className="text-sm text-[#475569] hover:text-[#005DE8] transition-colors">File Claim</Link>
            <button onClick={() => scrollTo('faq')} className="text-sm text-[#475569] hover:text-[#005DE8] transition-colors">FAQs</button>
          </div>
          <Link to="/login">
            <Button className="bg-[#005DE8] hover:bg-[#004BBA] rounded-md h-10 text-sm font-semibold" data-testid="navbar-login-button">
              Partner Login <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
        </div>
      </nav>

      {/* ===== HERO ===== */}
      <section className="bg-[#F9FAFB] min-h-[85vh] flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-7">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#0F172A] mb-6">
                Turn every phone sale<br />into a <span className="text-[#005DE8]">recurring income</span>
              </h1>
              <p className="text-lg text-[#475569] leading-relaxed mb-8 max-w-xl">
                Offer Mobikoo extended warranty plans to your customers and earn commission on every sale. Fully digital, zero paperwork.
              </p>
              <div className="flex flex-wrap gap-4 mb-6">
                <Button size="lg" variant="outline" className="rounded-md h-12 text-base border-[#E2E8F0] text-[#0F172A] px-8" onClick={() => scrollTo('how-it-works')}>
                  See how it works
                </Button>
              </div>
              <div className="flex items-center gap-6 text-sm text-[#475569]">
                <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-[#16A34A]" />Free to join</span>
                <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-[#16A34A]" />All brands covered</span>
                <span className="flex items-center gap-1.5"><CheckCircle className="h-4 w-4 text-[#16A34A]" />100% digital</span>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="bg-white border border-[#E2E8F0] rounded-xl p-6 shadow-[0_2px_10px_rgb(0,0,0,0.04)]">
                <h3 className="text-lg font-semibold text-[#0F172A] mb-1">Become a Partner</h3>
                <p className="text-sm text-[#475569] mb-4">Our team will reach out within 24 hours</p>
                <form onSubmit={handleLeadSubmit} data-testid="hero-lead-form">
                  <div className="space-y-3">
                    <div>
                      <Label className="text-xs text-slate-600">Shop / Company Name *</Label>
                      <Input placeholder="ABC Mobile Store" value={leadForm.company_name} onChange={(e) => handleChange('company_name', e.target.value)} className={inputCls} data-testid="hero-company-name" required />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">Owner Name *</Label>
                      <Input placeholder="John Doe" value={leadForm.owner_name} onChange={(e) => handleChange('owner_name', e.target.value)} className={inputCls} data-testid="hero-owner-name" required />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-600">Email *</Label>
                        <Input type="email" placeholder="you@example.com" value={leadForm.email} onChange={(e) => handleChange('email', e.target.value)} className={inputCls} data-testid="hero-email" required />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-600">Phone *</Label>
                        <Input type="tel" placeholder="+91 98765 43210" value={leadForm.phone} onChange={(e) => handleChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))} className={inputCls} data-testid="hero-phone" required />
                      </div>
                    </div>
                    <div>
                      <Label className="text-xs text-slate-600">GSTIN (Optional)</Label>
                      <Input placeholder="e.g., 27AABCU9603R1ZM" value={leadForm.gstin} onChange={(e) => handleChange('gstin', e.target.value)} className={inputCls} data-testid="hero-gstin" maxLength={15} />
                    </div>
                    <Button type="submit" className="w-full bg-[#005DE8] hover:bg-[#004BBA] h-11 text-base font-semibold rounded-md" disabled={submitting} data-testid="hero-submit">
                      {submitting ? 'Submitting...' : 'Get Started'} <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SOCIAL PROOF STRIP ===== */}
      <section className="bg-white border-y border-[#E2E8F0] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { num: '1000+', label: 'Active Partners' },
              { num: '1L+', label: 'Monthly Warranties' },
              { num: '60', label: 'Second to Register' },
              { num: '24/7', label: 'Support' },
            ].map((s, i) => (
              <div key={i} data-testid={`stat-${i}`}>
                <p className="text-3xl md:text-4xl font-bold text-[#005DE8]">{s.num}</p>
                <p className="text-sm text-[#475569] mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PARTNER BENEFITS ===== */}
      <section id="benefits" className="bg-[#F3F4F6] py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] mb-3">Everything you need to sell warranty, digitally</h2>
            <p className="text-[#475569] max-w-xl mx-auto">Join India's fastest growing extended warranty network for mobile phones</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Wallet, title: 'Earn commission on every sale', desc: 'Get instant commission credited for every warranty plan you sell. No delays.' },
              { icon: BarChart3, title: 'Digital dashboard', desc: 'Track warranties, claims, and commissions in real-time from any device.' },
              { icon: Users, title: 'Co-branded materials', desc: 'Customer-facing brochures and POS materials with your shop branding.' },
              { icon: Zap, title: 'Training & onboarding', desc: 'Dedicated onboarding support and product training for your staff.' },
              { icon: FileCheck, title: 'Zero paperwork', desc: 'Everything is digital — from plan activation to claim processing.' },
              { icon: Headphones, title: 'Dedicated partner support', desc: 'Priority support team assigned to your account for any queries.' },
            ].map((b, i) => (
              <div key={i} className="bg-white border border-[#E2E8F0] rounded-xl p-8 hover:shadow-[0_10px_30px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 bg-[#EEF2FF] rounded-lg flex items-center justify-center mb-5">
                  <b.icon className="h-6 w-6 text-[#005DE8]" />
                </div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{b.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="bg-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] mb-3">Get started in 3 simple steps</h2>
            <p className="text-[#475569]">Start selling warranty plans within 24 hours of registration</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-16 left-[20%] right-[20%] h-[2px] border-t-2 border-dashed border-[#CBD5E1]" />
            {[
              { step: '1', title: 'Register your shop', desc: 'Fill a quick form — we onboard you within 24 hours. No documents needed upfront.' },
              { step: '2', title: 'Sell warranty at counter', desc: 'Scan IMEI, select a plan, and issue warranty in seconds via the dashboard.' },
              { step: '3', title: 'Earn and grow', desc: 'Monthly commission payouts. Track everything on your real-time dashboard.' },
            ].map((s, i) => (
              <div key={i} className="text-center relative z-10">
                <div className="w-14 h-14 bg-[#005DE8] rounded-full flex items-center justify-center mx-auto mb-5 text-white text-xl font-bold">{s.step}</div>
                <h3 className="text-xl font-semibold text-[#0F172A] mb-2">{s.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed max-w-xs mx-auto">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PLATFORM FEATURES - 3 Column Pointers ===== */}
      <section className="bg-[#F9FAFB] py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] mb-3">A complete digital platform, built for shop owners</h2>
            <p className="text-[#475569] max-w-xl mx-auto">Manage your warranty sales, customer claims, and commissions in real-time</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Manage & Sell */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 hover:shadow-lg transition-shadow" data-testid="platform-manage-sell">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                  <LayoutDashboard className="h-5 w-5 text-[#005DE8]" />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A]">Manage & Sell</h3>
              </div>
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <Timer className="h-4 w-4 text-[#005DE8]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] text-sm">60 Sec Sales</p>
                    <p className="text-xs text-[#475569] mt-0.5">Sell warranties in seconds</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <BarChart3 className="h-4 w-4 text-[#005DE8]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] text-sm">Live Dashboard</p>
                    <p className="text-xs text-[#475569] mt-0.5">Track sales, revenue, trends</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <Settings className="h-4 w-4 text-[#005DE8]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] text-sm">Easy Integration</p>
                    <p className="text-xs text-[#475569] mt-0.5">Fits your existing workflow</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Customer Claim */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 hover:shadow-lg transition-shadow" data-testid="platform-customer-claim">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                  <FileWarning className="h-5 w-5 text-emerald-600" />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A]">Customer Claim</h3>
              </div>
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <BadgeCheck className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] text-sm">Zero Hassle</p>
                    <p className="text-xs text-[#475569] mt-0.5">Simple, smooth claim process</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <Zap className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] text-sm">Fast Approval</p>
                    <p className="text-xs text-[#475569] mt-0.5">Quick processing, minimal delays</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <Eye className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] text-sm">Full Tracking</p>
                    <p className="text-xs text-[#475569] mt-0.5">Real-time claim status updates</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Commission */}
            <div className="bg-white border border-[#E2E8F0] rounded-2xl p-8 hover:shadow-lg transition-shadow" data-testid="platform-commission">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
                  <DollarSign className="h-5 w-5 text-amber-600" />
                </div>
                <h3 className="text-lg font-bold text-[#0F172A]">Commission</h3>
              </div>
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <TrendingUp className="h-4 w-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] text-sm">Live Earnings</p>
                    <p className="text-xs text-[#475569] mt-0.5">Track commissions instantly</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <Gauge className="h-4 w-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] text-sm">High Margins</p>
                    <p className="text-xs text-[#475569] mt-0.5">Earn more per warranty</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                    <Banknote className="h-4 w-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#0F172A] text-sm">Quick Payouts</p>
                    <p className="text-xs text-[#475569] mt-0.5">Timely, reliable payments</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PLANS OVERVIEW ===== */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] mb-3">What your customers get</h2>
            <p className="text-[#475569]">Credible, clearly structured plans that your customers will love</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: '1-Year Extended Warranty', desc: 'Comprehensive coverage beyond manufacturer warranty. Covers all functional defects.', icon: ShieldCheck },
              { title: '100% Authorized Service', desc: 'Repairs through authorized service centers only. No third-party compromises.', icon: CheckCircle },
              { title: 'Accidental Damage Protection', desc: 'Coverage for screen cracks, liquid spills, and accidental drops.', badge: 'Coming Soon', icon: FileWarning },
            ].map((p, i) => (
              <div key={i} className="bg-white border border-[#E2E8F0] rounded-xl p-8 relative hover:shadow-[0_10px_30px_rgb(0,0,0,0.08)] transition-all duration-300">
                {p.badge && <span className="absolute top-4 right-4 text-xs font-semibold px-3 py-1 rounded-full bg-[#F3F4F6] text-[#475569]">{p.badge}</span>}
                <div className="w-12 h-12 bg-[#EEF2FF] rounded-lg flex items-center justify-center mb-5">
                  <p.icon className="h-6 w-6 text-[#005DE8]" />
                </div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{p.title}</h3>
                <p className="text-sm text-[#475569] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="bg-[#F3F4F6] py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] mb-3">Trusted by shop owners across India</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { quote: 'Mobikoo made warranty sales effortless. My staff activates plans in seconds, and the commissions are always on time.', name: 'Rajesh Sharma', shop: 'Sharma Mobiles', city: 'Pune' },
              { quote: 'The digital dashboard is a game-changer. I can track all warranty sales across my 3 stores from my phone.', name: 'Priya Patel', shop: 'PhoneZone', city: 'Mumbai' },
              { quote: 'Customer complaints reduced dramatically after we started selling Mobikoo plans. The claims process is super smooth.', name: 'Vikram Joshi', shop: 'V-Mobile Hub', city: 'Nashik' },
            ].map((t, i) => (
              <div key={i} className="bg-white border border-[#E2E8F0] rounded-xl p-8">
                <div className="flex gap-1 mb-4">{[1,2,3,4,5].map(s => <Star key={s} className="h-4 w-4 fill-[#005DE8] text-[#005DE8]" />)}</div>
                <p className="text-[#475569] leading-relaxed mb-6">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#005DE8] flex items-center justify-center text-white font-bold text-sm">{t.name.charAt(0)}</div>
                  <div>
                    <p className="font-semibold text-[#0F172A] text-sm">{t.name}</p>
                    <p className="text-xs text-[#475569]">{t.shop}, {t.city}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section id="faq" className="bg-white py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#0F172A] mb-3">Frequently asked questions</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {[
              { q: 'How do I register as a Mobikoo partner?', a: 'Simply fill the form above or visit the registration page. Our team will verify your shop details and activate your account within 24 hours. No upfront documents needed.' },
              { q: 'How and when is commission paid?', a: 'Commissions are calculated monthly based on warranty plans sold. Payments are made via bank transfer by the 10th of every month for the previous month\'s sales.' },
              { q: 'What happens when a customer claims warranty?', a: 'Customers can raise a claim through Mobikoo. We handle the entire claim process — from pickup to repair to delivery. Your shop is notified at every step.' },
              { q: 'Is training provided for my staff?', a: 'Yes! We provide complete onboarding training, product guides, and ongoing support. Your dedicated account manager will help your team get started.' },
              { q: 'Can I use my own branding with Mobikoo?', a: 'Absolutely. We provide co-branded marketing materials and POS displays that feature both Mobikoo and your shop branding.' },
              { q: 'Which phone brands are covered?', a: 'All major mobile phone brands are covered including Apple, Samsung, OnePlus, Xiaomi, Realme, Oppo, Vivo, and more.' },
            ].map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border border-[#E2E8F0] rounded-lg px-6" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-semibold text-[#0F172A] text-base hover:no-underline py-4">{f.q}</AccordionTrigger>
                <AccordionContent className="text-[#475569] pb-4 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="bg-[#005DE8] py-20 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-4">Start earning with Mobikoo — join free today</h2>
            <p className="text-blue-100 max-w-lg mx-auto">Takes less than 5 minutes. No setup fee. Start selling warranty plans within 24 hours.</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
            <form onSubmit={handleLeadSubmit} data-testid="cta-lead-form">
              <div className="space-y-3">
                <div>
                  <Label className="text-white/80 text-xs">Shop / Company Name *</Label>
                  <Input placeholder="ABC Mobile Store" value={leadForm.company_name} onChange={(e) => handleChange('company_name', e.target.value)} className={inputClsDark} data-testid="cta-company-name" required />
                </div>
                <div>
                  <Label className="text-white/80 text-xs">Owner Name *</Label>
                  <Input placeholder="John Doe" value={leadForm.owner_name} onChange={(e) => handleChange('owner_name', e.target.value)} className={inputClsDark} data-testid="cta-owner-name" required />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-white/80 text-xs">Email *</Label>
                    <Input type="email" placeholder="you@example.com" value={leadForm.email} onChange={(e) => handleChange('email', e.target.value)} className={inputClsDark} data-testid="cta-email" required />
                  </div>
                  <div>
                    <Label className="text-white/80 text-xs">Phone *</Label>
                    <Input type="tel" placeholder="+91 98765 43210" value={leadForm.phone} onChange={(e) => handleChange('phone', e.target.value.replace(/\D/g, '').slice(0, 10))} className={inputClsDark} data-testid="cta-phone" required />
                  </div>
                </div>
                <div>
                  <Label className="text-white/80 text-xs">GSTIN (Optional)</Label>
                  <Input placeholder="e.g., 27AABCU9603R1ZM" value={leadForm.gstin} onChange={(e) => handleChange('gstin', e.target.value)} className={inputClsDark} data-testid="cta-gstin" maxLength={15} />
                </div>
                <Button type="submit" className="w-full bg-white text-[#005DE8] hover:bg-gray-100 h-11 text-base font-semibold rounded-md" disabled={submitting} data-testid="cta-submit">
                  {submitting ? 'Submitting...' : 'Get Started'} <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-white border-t border-[#E2E8F0] py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-[#005DE8] rounded-lg flex items-center justify-center"><ShieldCheck className="h-4 w-4 text-white" /></div>
                <span className="text-lg font-bold text-[#0F172A]">Mobikoo</span>
              </div>
              <p className="text-sm text-[#475569] leading-relaxed">India's trusted extended warranty platform for mobile devices.</p>
            </div>
            <div>
              <h4 className="font-semibold text-[#0F172A] mb-4 text-sm">Quick Links</h4>
              <div className="space-y-2 text-sm text-[#475569]">
                <Link to="/register" className="block hover:text-[#005DE8] transition-colors">Become a Partner</Link>
                <Link to="/login" className="block hover:text-[#005DE8] transition-colors">Partner Login</Link>
                <Link to="/verify" className="block hover:text-[#005DE8] transition-colors">Check Warranty</Link>
                <Link to="/claim" className="block hover:text-[#005DE8] transition-colors">File a Claim</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-[#0F172A] mb-4 text-sm">Legal</h4>
              <div className="space-y-2 text-sm text-[#475569]">
                <p>GSTIN: 27ENGPA3125K1Z5</p>
                <Link to="/privacy" className="block hover:text-[#005DE8] transition-colors">Privacy Policy</Link>
                <Link to="/terms" className="block hover:text-[#005DE8] transition-colors">Terms & Conditions</Link>
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-[#0F172A] mb-4 text-sm">Contact</h4>
              <div className="space-y-2 text-sm text-[#475569]">
                <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 shrink-0 text-[#475569]" /><span>2228, EFC Business Center, JK Infotech, Hinjewadi Phase 1, Pune, 411057</span></div>
                <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-[#475569]" /><span>+91 8600862333</span></div>
                <div className="flex items-center gap-2"><Mail className="h-4 w-4 text-[#475569]" /><span>support@mobikoo.com</span></div>
              </div>
            </div>
          </div>
          <div className="border-t border-[#E2E8F0] mt-10 pt-6 text-center text-sm text-[#64748B]">
            &copy; {new Date().getFullYear()} Mobikoo. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
